import React, { FC, useState } from 'react';
import { Stalled, StalledContent } from 'QuoteApp/components';
import { Asset, Button, Modal, UserOutlineIcon } from '@clearcover/atlas';
import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { DriverRemovalModal } from 'QuoteApp/components/DriverRemovalModal';
import {
  DriverDataType,
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers';
import { formatDriverDisplayFromDriverData } from 'QuoteApp/utils';
import styles from './styles.module.css';

export const MaxDriversExceeded: FC = () => {
  const { appId, details, config, setDetails, fetchRate } = useAppContext();
  const { toast } = useToast();
  const [{ error: deleteDriverError }, deleteDriver] =
    useLazyQuoteApi<GetDetailsResponseType>({
      method: 'POST',
    });

  const [deletingDriver, setDeletingDriver] = useState<boolean>(false);

  const additionalDrivers = Object.keys(details.drivers).reduce<
    Array<DriverDataType>
  >((acc, driverId) => {
    if (details.drivers[driverId].discoveredDriver) {
      acc.push(details.drivers[driverId]);
    }
    return acc;
  }, []);

  if (!config) {
    return null;
  }

  return (
    <Stalled testId="max-drivers-exceeded-page">
      <StalledContent variant="headline">
        Maximum Drivers Exceeded
      </StalledContent>
      <StalledContent>
        We've found the following additional drivers, putting the number of
        drivers on the application over the limit
        {config.drivers.maxDrivers ? ` of ${config.drivers.maxDrivers}` : ''}.
      </StalledContent>
      <StalledContent>
        To continue this quote, you can remove any added drivers who don't live
        in the same household as the applicant, or are covered by another
        policy.
      </StalledContent>
      <ul className={styles.container}>
        {additionalDrivers.map((driver, i) => {
          const driverLabel = formatDriverDisplayFromDriverData(driver, config);

          return (
            <li key={driver.identifier}>
              <Asset
                className={styles.icon}
                icon={UserOutlineIcon}
                description="user-icon"
                height={20}
                width={20}
              />
              <span className={styles.name}>{driverLabel}</span>
              <Modal
                variant="normal"
                toggle={(openModal) => (
                  <Button
                    data-testid={`remove-driver-${i + 1}`}
                    variant="danger"
                    size="sm"
                    disabled={false}
                    onClick={openModal}
                    className={styles.button}
                  >
                    Remove
                  </Button>
                )}
              >
                {(close) => (
                  <DriverRemovalModal
                    driverId={driver.identifier}
                    onSubmit={(data) => {
                      setDeletingDriver(true);
                      deleteDriver({
                        url: `/insurance_applications/${appId}/drivers/${driver.identifier}/delete`,
                        data,
                        transformResponse: transformAppDetails,
                      })
                        .then(async (response) => {
                          if (response.data.status === 'open') {
                            // max driver stop comes after calling rate, so we need to check before setting app details
                            const uwPassed = await fetchRate();
                            if (uwPassed) {
                              // when uw fails, fetchRate returns null and refreshes details itself
                              // otherwise, we need to set it ourselves from the deleteDriver response
                              setDetails(response.data);
                            }
                          } else {
                            // fallback; this would be called if the app was still stopped but may not be a reachable state in reality
                            setDetails(response.data);
                          }
                          toast({
                            message: `${driverLabel} has been successfully deleted.`,
                            type: 'success',
                            withSecondaryNav: false,
                          });
                        })
                        .finally(() => setDeletingDriver(false));
                    }}
                    close={close}
                    isSubmitting={deletingDriver}
                    error={deleteDriverError?.message}
                    driverLabel={`Confirm Removal of ${driverLabel}`}
                  />
                )}
              </Modal>
            </li>
          );
        })}
      </ul>
      <StalledContent>
        If you have already removed all non-ratable drivers and the applicant is
        still over the limit, contact a Clearcover Agent Advocate by chat or
        call us at 855-809-2965 for help distributing them across separate
        policies.
      </StalledContent>
    </Stalled>
  );
};
